import type { DataItem } from '@ricoh/ria.client.core/types/component/data.type';
import type { ComparableType } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type { MetaDataExtractionType } from '~/common/model/api/batch-type';
import type {
  BusinessRule,
  BusinessRuleCalculateDurationBetweenDates,
  BusinessRuleCalculateValue,
  BusinessRuleConvertValue,
  BusinessRuleExecuteRegularExpression,
  BusinessRuleModifyString,
  BusinessRuleQueryDatabase,
  BusinessRuleSetField,
  BusinessRuleSetVariable,
} from '~/common/model/api/batch-type/business-rule';
import type { DataType, DataTypeList, DataTypeName, DataTypeNameGeneral } from '~/common/model/api/common';

import type { PatternValueSetsData } from './_pattern-type.type';

const META_DATA_EXTRACTION_MAPPING: Partial<Record<DataTypeNameGeneral, MetaDataExtractionType[]>> = {
  text: ['from', 'subject', 'body_content', 'message_id', 'to_recipients', 'cc_recipients', 'message_importance', 'parent_folder', 'flag_status', 'file_name'],
  integer: ['attachments_count'],
  decimal: ['file_size'],
  datetime: ['received_date_time'],
};

const SYSTEM_VALUES_MAPPING: Record<DataTypeNameGeneral, string[]> = {
  text: [
    'guid',
    'random',
    'batchId',
    'batchName',
    'batchType',
    'batchPriority',
    'documentName',
    'documentType',
    'createdBy',
    'createdByEmail',
    'lastUpdatedBy',
    'lastUpdatedByEmail',
    'filePdfBase64',
  ],
  integer: ['documentCount'],
  decimal: ['separationConfidence', 'classificationConfidence', 'extractionConfidence'],
  datetime: ['datetime', 'createdAt', 'lastUpdatedAt'],
  date: ['date', 'createdAtDate', 'lastUpdatedAtDate'],
  time: ['time', 'createdAtTime', 'lastUpdatedAtTime'],
  boolean: [],
  currency: [],
};

const isSystemValueType: Partial<Record<DataTypeName, Func<[dataItem: DataItem], boolean>>> = {
  'text': (dataItem) => SYSTEM_VALUES_MAPPING.text.includes(dataItem.value),
  'integer': (dataItem) => SYSTEM_VALUES_MAPPING.integer.includes(dataItem.value),
  'decimal': (dataItem) => SYSTEM_VALUES_MAPPING.decimal.includes(dataItem.value),
  'datetime': (dataItem) => SYSTEM_VALUES_MAPPING.datetime.includes(dataItem.value),
  'date': (dataItem) => SYSTEM_VALUES_MAPPING.date.includes(dataItem.value),
  'time': (dataItem) => SYSTEM_VALUES_MAPPING.time.includes(dataItem.value),
  'list.text': (dataItem) => SYSTEM_VALUES_MAPPING.text.includes(dataItem.value),
  'list.integer': (dataItem) => SYSTEM_VALUES_MAPPING.integer.includes(dataItem.value),
  'list.decimal': (dataItem) => SYSTEM_VALUES_MAPPING.decimal.includes(dataItem.value),
  'list.datetime': (dataItem) => SYSTEM_VALUES_MAPPING.datetime.includes(dataItem.value),
  'list.date': (dataItem) => SYSTEM_VALUES_MAPPING.date.includes(dataItem.value),
  'list.time': (dataItem) => SYSTEM_VALUES_MAPPING.time.includes(dataItem.value),
};

/**
 * Create SystemValues Data MatchingType.
 *
 * @param {DataTypeName} typeName Type name.
 * @param {DataItem[]} data SystemValues.
 *
 * @returns {DataItem[]} SystemValues DataItem.
 */
function createSystemValuesDataMatchingType(typeName?: DataTypeName, data?: DataItem[]): DataItem[] {
  if (!data) {
    return [];
  }

  if (!typeName) {
    return data;
  }

  const predicate = isSystemValueType[typeName] ?? (() => false);
  return predicate ? data.filter(predicate) : [];
}

/**
 * Create ValueSets Data MatchingType.
 *
 * @param {DataTypeName} typeName Type name.
 * @param {PatternValueSetsData[]} data ValueSets.
 *
 * @returns {DataItem[]} ValueSets DataItem.
 */
function createValueSetsDataMatchingType(typeName?: DataTypeName, data?: PatternValueSetsData[]): DataItem[] {
  if (!data) {
    return [];
  }

  if (!typeName) {
    return data.map<DataItem>(({ name, displayName }) => ({
      text: displayName ?? name,
      value: name,
    }));
  }

  return data.reduce<DataItem[]>((prev, item) => {
    const isGeneralAndTableTypeSupport = !typeName.includes('list') && typeName === item.type.name;

    //* For list type, just support the value sets with the same general type, e.g. list.text support the value set with text type
    const isListTypeSupportWithGeneralType = typeName.includes('list') && typeName.replace('list.', '') === item.type.name;

    if (isGeneralAndTableTypeSupport || isListTypeSupportWithGeneralType) {
      prev.push({
        text: item.displayName ?? item.name,
        value: item.name,
      });
    }

    return prev;
  }, []);
}

/**
 * Create BusinessRules Data MatchingType.
 *
 * @param {DataTypeName} typeName Type name.
 * @param {BusinessRule[]} data BusinessRules.
 *
 * @returns {DataItem[]} BusinessRules DataItem.
 */
function createBusinessRulesDataMatchingType(typeName?: DataTypeName, data?: BusinessRule[]): DataItem[] {
  if (!data) {
    return [];
  }

  if (!typeName) {
    return data.reduce<DataItem[]>((prev, rule) => {
      if (!(
        isType<BusinessRuleSetField>(rule, 'setField') ||
        isType<BusinessRuleSetVariable>(rule, 'setVariable') ||
        (isType<BusinessRuleQueryDatabase>(rule, 'queryDatabase') && rule.pattern.$is !== 'count')
      )) {
        prev.push({
          text: rule.displayName,
          value: rule.name,
        });
      }

      return prev;
    }, []);
  }

  if (typeName === 'text') {
    return data.reduce<DataItem[]>((prev, rule) => {
      if (
        // * Execute Regular Expression
        (isType<BusinessRuleExecuteRegularExpression>(rule, 'executeRegularExpression') &&
          (rule.pattern.operation === 'match' || rule.pattern.operation === 'replace')) ||
        // * Modify String
        (isType<BusinessRuleModifyString>(rule, 'modifyString') && rule.pattern.operations[rule.pattern.operations.length - 1]?.operation !== 'find') ||
        // * Convert Value
        (isType<BusinessRuleConvertValue>(rule, 'convertValue') && rule.pattern.targetType === typeName)
      ) {
        prev.push({
          text: rule.displayName,
          value: rule.name,
        });
      }

      return prev;
    }, []);
  }

  if (typeName === 'integer') {
    return data.reduce<DataItem[]>((prev, rule) => {
      if (
        // * Calculate Value
        isType<BusinessRuleCalculateValue>(rule, 'calculateValue') ||
        // * Calculate Duration Between Dates
        isType<BusinessRuleCalculateDurationBetweenDates>(rule, 'calculateDurationBetweenDates') ||
        // * Convert Value
        (isType<BusinessRuleConvertValue>(rule, 'convertValue') && rule.pattern.targetType === typeName) ||
        // * Query Azure Sql Count
        (isType<BusinessRuleQueryDatabase>(rule, 'queryDatabase') && rule.pattern.$is === 'count')
      ) {
        prev.push({
          text: rule.displayName,
          value: rule.name,
        });
      }

      return prev;
    }, []);
  }

  if (typeName === 'decimal') {
    return data.reduce<DataItem[]>((prev, rule) => {
      if (
        // * Calculate Value
        isType<BusinessRuleCalculateValue>(rule, 'calculateValue') ||
        // * Calculate Duration Between Dates
        isType<BusinessRuleCalculateDurationBetweenDates>(rule, 'calculateDurationBetweenDates') ||
        // * Convert Value
        (isType<BusinessRuleConvertValue>(rule, 'convertValue') && rule.pattern.targetType === typeName)
      ) {
        prev.push({
          text: rule.displayName,
          value: rule.name,
        });
      }

      return prev;
    }, []);
  }

  if (typeName === 'currency') {
    return data.reduce<DataItem[]>((prev, rule) => {
      if (
        // * Convert Value
        isType<BusinessRuleConvertValue>(rule, 'convertValue') &&
        rule.pattern.targetType === typeName
      ) {
        prev.push({
          text: rule.displayName,
          value: rule.name,
        });
      }

      return prev;
    }, []);
  }

  if (typeName === 'boolean') {
    return data.reduce<DataItem[]>((prev, rule) => {
      if (
        // * Convert Value
        (isType<BusinessRuleConvertValue>(rule, 'convertValue') && rule.pattern.targetType === typeName) ||
        // * Execute Regular Expression
        (isType<BusinessRuleExecuteRegularExpression>(rule, 'executeRegularExpression') && rule.pattern.operation === 'is_match') ||
        (isType<BusinessRuleModifyString>(rule, 'modifyString') && rule.pattern.operations[rule.pattern.operations.length - 1]?.operation === 'find')
      ) {
        prev.push({
          text: rule.displayName,
          value: rule.name,
        });
      }

      return prev;
    }, []);
  }

  if (typeName === 'datetime' || typeName === 'date' || typeName === 'time') {
    return data.reduce<DataItem[]>((prev, rule) => {
      if (
        // * Convert Value
        isType<BusinessRuleConvertValue>(rule, 'convertValue') &&
        rule.pattern.targetType === typeName
      ) {
        prev.push({
          text: rule.displayName,
          value: rule.name,
        });
      }

      return prev;
    }, []);
  }

  if (typeName === 'table') {
    return data.reduce<DataItem[]>((prev, rule) => {
      if (
        // * Query Azure Sql Select
        isType<BusinessRuleQueryDatabase>(rule, 'queryDatabase') &&
        rule.pattern.$is === 'select'
      ) {
        prev.push({
          text: rule.displayName,
          value: rule.name,
        });
      }

      return prev;
    }, []);
  }

  return [];
}

/** Create Data MatchingType. */
export const createDataMatchingType = {
  systemValues: createSystemValuesDataMatchingType,
  globalVariables: createValueSetsDataMatchingType,
  batchFields: createValueSetsDataMatchingType,
  batchVariables: createValueSetsDataMatchingType,
  batchBusinessRules: createBusinessRulesDataMatchingType,
  documentFields: createValueSetsDataMatchingType,
  documentVariables: createValueSetsDataMatchingType,
  documentBusinessRules: createBusinessRulesDataMatchingType,
};

/**
 * Create Metadata Data MatchingType.
 *
 * @param {MetaDataExtractionType[]} metadata Metadata metadata.
 * @param {DataTypeName} typeName Type name.
 *
 * @returns {MetaDataExtractionType[]} Metadata metadata.
 */
export function createMetadataMatchingType(metadata: MetaDataExtractionType[], typeName?: DataTypeName): MetaDataExtractionType[] {
  if (!typeName || !metadata) {
    return [];
  }

  //* Not support metadata with type list yet
  if (typeName.includes('list') as unknown as DataTypeList) {
    return [];
  }

  return META_DATA_EXTRACTION_MAPPING[typeName as DataTypeNameGeneral] ?? [];
}

/**
 * Get Business Rule Output Type.
 *
 * @param {string} ruleName Rule name.
 * @param {BusinessRule[]} rules Business rules.
 *
 * @returns {DataType} Business rule output type.
 */
export function getBusinessRuleOutputType(ruleName: string, rules?: BusinessRule[]): DataType {
  const rule = rules?.find(({ name }) => name === ruleName);
  if (!rule) {
    return { name: 'text' }; // Default fallback
  }

  switch (rule.$is) {
    case 'calculateValue':
    case 'calculateDurationBetweenDates': {
      return { name: 'integer' };
    }

    case 'executeRegularExpression': {
      if (rule.pattern.operation === 'is_match') {
        return { name: 'boolean' };
      }

      return { name: 'text' };
    }

    case 'modifyString': {
      const lastOperation = rule.pattern.operations.at(-1);
      if (lastOperation?.operation === 'find') {
        return { name: 'boolean' };
      }

      return { name: 'text' };
    }

    case 'convertValue': {
      return { name: rule.pattern.targetType };
    }

    case 'queryDatabase': {
      if (rule.pattern.$is === 'select') {
        return { name: 'table' };
      }

      if (rule.pattern.$is === 'count' || rule.pattern.$is === 'insert' || rule.pattern.$is === 'update' || rule.pattern.$is === 'delete') {
        return { name: 'integer' };
      }

      return { name: 'text' };
    }

    default: {
      return { name: 'text' };
    }
  }
}

/**
 * Get System Value Output Type.
 *
 * @param {ComparableType} systemValueName System value name.
 *
 * @returns {DataType} System Value type.
 */
export function getSystemValueOutputType(systemValueName: ComparableType): DataType {
  const typeName = Object.keys<DataTypeNameGeneral>(SYSTEM_VALUES_MAPPING).find((key) => SYSTEM_VALUES_MAPPING[key].includes(systemValueName));
  return {
    name: typeName,
  } as DataType;
}
